import { ELEMENT_LIST_HEADER_HEIGHT } from './element-list.constants';

/** Fallback when `scrollend` is missing or never fires (already at target). */
const SCROLL_SETTLE_FALLBACK_MS = 400;

/**
 * Scroll root for an element-list node: {@link HTMLElement.closest} `main`.
 */
export const getListScrollRoot = (element: HTMLElement): HTMLElement | null =>
  element.closest('main');

export const LIST_SCROLL_HEIGHT_VAR = '--element-list-scroll-height';

/**
 * Keep only enough content height to support the current scroll position.
 * Natural content can be taller; when rows collapse, the list's flex filler
 * takes up the difference without letting the browser clamp scrollTop.
 * Scrolling back up releases that space, instead of reserving a viewport of
 * empty rows permanently (DEV-3423).
 */
export const trackListScrollHeight = (content: HTMLElement): (() => void) => {
  const scrollRoot = getListScrollRoot(content);
  if (!scrollRoot) return () => undefined;

  const update = (): void => {
    // Match integer scrollHeight/clientHeight metrics. Rounding up fractional
    // scrollTop can otherwise add another pixel at the bottom on every scroll.
    const height =
      Math.max(0, Math.floor(scrollRoot.scrollTop)) + scrollRoot.clientHeight;
    content.style.setProperty(LIST_SCROLL_HEIGHT_VAR, `${height}px`);
  };

  update();
  scrollRoot.addEventListener('scroll', update, { passive: true });
  const observer = new ResizeObserver(update);
  observer.observe(scrollRoot);

  return () => {
    scrollRoot.removeEventListener('scroll', update);
    observer.disconnect();
    content.style.removeProperty(LIST_SCROLL_HEIGHT_VAR);
  };
};

/** The element-list scrollport (`main`), or `null` before layout. */
export const getMainListScrollRoot = (): HTMLElement | null =>
  document.querySelector('main');

/** Current `scrollTop` of {@link getMainListScrollRoot}, or `0` if missing. */
export const readMainListScrollTop = (): number =>
  getMainListScrollRoot()?.scrollTop ?? 0;

/** Set `scrollTop` on {@link getMainListScrollRoot} when it exists. */
export const writeMainListScrollTop = (scrollTop: number): void => {
  const root = getMainListScrollRoot();
  if (root) root.scrollTop = scrollTop;
};

/**
 * Offset below the scrollport top where a sticky file/group header parks
 * (element-list tabs / search chrome).
 */
export const getStickyListOffsetPx = (): number => ELEMENT_LIST_HEADER_HEIGHT;

type ScrollRectBox = {
  scrollTop: number;
  getBoundingClientRect: () => Pick<DOMRect, 'top'>;
};

/**
 * Scroll offset that parks {@link element} at the top of {@link scrollRoot},
 * minus {@link stickyOffsetPx} (list chrome that lives inside the same
 * scrollport).
 */
export const parkedListScrollTop = (
  scrollRoot: ScrollRectBox,
  element: { getBoundingClientRect: () => Pick<DOMRect, 'top'> },
  stickyOffsetPx: number,
): number =>
  Math.max(
    0,
    scrollRoot.scrollTop +
      element.getBoundingClientRect().top -
      scrollRoot.getBoundingClientRect().top -
      stickyOffsetPx,
  );

/**
 * Scrolls the list root so {@link element} sits just below the sticky
 * element-list header (tabs / search chrome).
 *
 * Uses the browser's native smooth scroll so animation stays fluid while React
 * is still mounting import rows (a JS rAF loop janks on that path).
 *
 * {@link options.onSettled} runs when the scroll finishes, or immediately when
 * no movement is needed.
 */
export const scrollElementBelowListHeader = (
  element: HTMLElement,
  options?: { behavior?: ScrollBehavior; onSettled?: () => void },
): (() => void) => {
  const scrollRoot = getListScrollRoot(element);
  const settle = options?.onSettled;
  if (!scrollRoot) {
    settle?.();
    return () => undefined;
  }

  const top = parkedListScrollTop(scrollRoot, element, getStickyListOffsetPx());
  if (Math.abs(top - scrollRoot.scrollTop) < 1) {
    settle?.();
    return () => undefined;
  }

  const behavior = options?.behavior ?? 'smooth';
  if (behavior === 'auto' || !settle) {
    scrollRoot.scrollTo({ top: Math.max(0, top), behavior });
    settle?.();
    return () => undefined;
  }

  let settled = false;
  const finish = (): void => {
    if (settled) return;
    settled = true;
    scrollRoot.removeEventListener('scrollend', finish);
    window.clearTimeout(fallbackId);
    settle();
  };
  const fallbackId = window.setTimeout(finish, SCROLL_SETTLE_FALLBACK_MS);
  scrollRoot.addEventListener('scrollend', finish, { once: true });
  scrollRoot.scrollTo({ top: Math.max(0, top), behavior });

  return () => {
    settled = true;
    scrollRoot.removeEventListener('scrollend', finish);
    window.clearTimeout(fallbackId);
  };
};

/**
 * When collapsing a sticky import file mid-scroll, jumps the list root so
 * {@link section} parks under the list chrome. Without this, leftover
 * scroll slack leaves a blank gap where the rows were.
 *
 * Pass the section wrapper (not the sticky header): sticky `getBoundingClientRect`
 * reports the stuck visual top, while the wrapper keeps the document-flow top.
 */
export const scrollCollapsedStickySectionIntoPlace = (
  section: HTMLElement,
  options?: { behavior?: ScrollBehavior },
): void => {
  const scrollRoot = getListScrollRoot(section);
  if (!scrollRoot) return;

  const rootRect = scrollRoot.getBoundingClientRect();
  const sectionRect = section.getBoundingClientRect();
  const stickyLine = rootRect.top + getStickyListOffsetPx();
  // Only correct when the section started above the sticky parking line.
  if (sectionRect.top >= stickyLine - 1) return;

  scrollRoot.scrollTo({
    top: Math.max(0, scrollRoot.scrollTop + sectionRect.top - stickyLine),
    behavior: options?.behavior ?? 'auto',
  });
};

/**
 * Run {@link fn} after the next two animation frames so the current paint has
 * committed and layout (e.g. newly mounted import rows) has settled.
 */
export const afterNextPaint = (fn: () => void): (() => void) => {
  let cancelled = false;
  let outer = 0;
  let inner = 0;
  outer = requestAnimationFrame(() => {
    inner = requestAnimationFrame(() => {
      if (!cancelled) fn();
    });
  });
  return () => {
    cancelled = true;
    cancelAnimationFrame(outer);
    cancelAnimationFrame(inner);
  };
};

/** ~400ms at 60fps — enough for an Import-tab mount without hanging a hidden node. */
const LAYOUT_WAIT_MAX_FRAMES = 24;

/**
 * Calls {@link onReady} on the first animation frame where {@link isReady} is
 * true, or after {@link maxFrames} frames if it never is (so a last-chance
 * scroll can still run). Two frames is often too early after an Import tab
 * switch — the file header still has a zero rect and parking becomes a no-op.
 *
 * Returns a cancel function; after cancel, {@link onReady} will not run.
 */
export const untilNextLayout = (
  isReady: () => boolean,
  onReady: () => void,
  maxFrames = LAYOUT_WAIT_MAX_FRAMES,
): (() => void) => {
  let cancelled = false;
  let attempts = 0;
  let id = 0;
  const tick = (): void => {
    if (cancelled) return;
    attempts += 1;
    if (isReady() || attempts >= maxFrames) {
      onReady();
      return;
    }
    id = requestAnimationFrame(tick);
  };
  id = requestAnimationFrame(tick);
  return () => {
    cancelled = true;
    cancelAnimationFrame(id);
  };
};

/**
 * Scroll top to apply when switching element-list tabs.
 * `null` means leave the current scroll (first paint or same tab).
 * Suppression (new-file drop) lands at 0 so the file can park after paint.
 *
 * Positions are per tab so centering a product on Elements does not overwrite
 * the Import view the user left.
 */
export const resolveListTabScrollRestore = <TTab extends string>(options: {
  prevTab: TTab | undefined;
  nextTab: TTab;
  savedByTab: Partial<Record<TTab, number>>;
  suppressRestore: boolean;
}): number | null => {
  const { prevTab, nextTab, savedByTab, suppressRestore } = options;
  if (prevTab === undefined || prevTab === nextTab) return null;
  if (suppressRestore) return 0;
  return savedByTab[nextTab] ?? 0;
};

/**
 * Predicate that turns true once {@link getElement} has a non-zero box and its
 * parked scroll offset matched the previous call. Use with
 * {@link untilNextLayout} so Import-tab parking waits until proposals / chrome
 * above the file have finished laying out.
 */
export const createParkedListTargetStabilizer = (
  getElement: () => HTMLElement | null,
): (() => boolean) => {
  let lastTop: number | null = null;
  return () => {
    const element = getElement();
    if (!element || element.getBoundingClientRect().height < 1) return false;
    const scrollRoot = getListScrollRoot(element);
    if (!scrollRoot) return false;
    const top = parkedListScrollTop(
      scrollRoot,
      element,
      getStickyListOffsetPx(),
    );
    const stable = lastTop !== null && lastTop === top;
    lastTop = top;
    return stable;
  };
};

/**
 * Waits until {@link getSection} has a stable parked offset, then scrolls
 * so the section sits under the list chrome.
 */
export const parkListSectionWhenReady = (
  getSection: () => HTMLElement | null,
  options?: { behavior?: ScrollBehavior; onSettled?: () => void },
): (() => void) => {
  let cancelScroll: (() => void) | undefined;
  const cancelWait = untilNextLayout(
    createParkedListTargetStabilizer(getSection),
    () => {
      const section = getSection();
      if (!section) {
        options?.onSettled?.();
        return;
      }
      cancelScroll = scrollElementBelowListHeader(section, options);
    },
  );
  return () => {
    cancelWait();
    cancelScroll?.();
  };
};
